import { useEffect, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";
import ProfileCompletionPage from "@/pages/ProfileCompletionPage";
import StudentDashboard from "@/pages/StudentDashboard";
import SMEDashboard from "@/pages/SMEDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import PracticePage from "@/pages/PracticePage";
import PracticeSelectPage from "@/pages/PracticeSelectPage";   // NEW
import StudentProfilePage from "@/pages/StudentProfilePage";   // NEW
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AttemptsPage from "@/pages/AttemptsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios to send auth token with every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  // Helper to save token
  const saveToken = (token) => {
    localStorage.setItem('session_token', token);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, saveToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#2E3A59] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[#2E3A59] font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role access
  if (allowedRoles && !allowedRoles.includes(user.role || "student")) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if profile completion needed
  if (!user.profile_completed && location.pathname !== "/complete-profile") {
    return <Navigate to="/complete-profile" replace />;
  }

  // Check if onboarding needed
  if (user.profile_completed && !user.onboarding_completed && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Dashboard Router - routes to correct dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  const role = user?.role || "student";

  switch (role) {
    case "admin":
      return <AdminDashboard />;
    case "sme":
      return <SMEDashboard />;
    default:
      return <StudentDashboard />;
  }
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Profile Setup Routes */}
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute>
            <ProfileCompletionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      
      {/* Main Dashboard - Role-based */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      
      {/* Student Routes */}
      <Route
        path="/practice/:subject"
        element={
          <ProtectedRoute>
            <PracticePage />
          </ProtectedRoute>
        }
      />

      {/* FIX: /practice now shows subject picker instead of redirecting */}
      <Route
        path="/practice"
        element={
          <ProtectedRoute>
            <PracticeSelectPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/attempts"
        element={
          <ProtectedRoute>
            <AttemptsPage />
          </ProtectedRoute>
        }
      />

      {/* FIX: /profile now shows full student profile instead of StudentDashboard */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <StudentProfilePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      
      {/* SME Routes */}
      <Route
        path="/sme/dashboard"
        element={
          <ProtectedRoute allowedRoles={["sme", "admin"]}>
            <SMEDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sme/review"
        element={
          <ProtectedRoute allowedRoles={["sme", "admin"]}>
            <SMEDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sme/questions"
        element={
          <ProtectedRoute allowedRoles={["sme", "admin"]}>
            <SMEDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/questions"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Fallback */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-[#F8FAFC]">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;